import type { Component } from '../schemas/component';
import type { ReleaseComponent } from '../schemas/release-component';

/**
 * One row of Catalog → Components, whichever list it came from.
 *
 * `GET /components` and `GET /release-components` stay separate routes with
 * separate schemas; this is the only place the two meet, so the table can show
 * one list without either shape learning about the other.
 */
export interface CatalogComponentRow {
    /** `${type}:${id}`. A release and a commercial component may share an id. */
    readonly key: string;
    readonly type: 'commercial' | 'release';
    readonly id: string;
    readonly name: string;
    readonly description: string;
    readonly flagKeys: readonly string[];
    /** Commercial only. */
    readonly owner?: string;
    /** Release only: the release label. */
    readonly version?: string;
}

const byName = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

export function mergeComponentLists(
    commercial: readonly Component[],
    releases: readonly ReleaseComponent[],
): CatalogComponentRow[] {
    const rows: CatalogComponentRow[] = [
        ...commercial.map((c): CatalogComponentRow => ({
            key: `commercial:${c.id}`,
            type: 'commercial',
            id: c.id,
            name: c.displayName,
            description: c.description,
            flagKeys: c.flagKeys,
            owner: c.owner,
        })),
        ...releases.map((r): CatalogComponentRow => ({
            key: `release:${r.id}`,
            type: 'release',
            id: r.id,
            name: r.name,
            description: r.description,
            flagKeys: r.flagKeys,
            version: r.releaseVersion,
        })),
    ];

    return rows.sort((a, b) => byName.compare(a.name, b.name) || byName.compare(a.id, b.id));
}

/** The words on the Type badge. */
export function componentTypeLabel(type: CatalogComponentRow['type']): string {
    return type === 'release' ? 'Release' : 'Commercial';
}

/** Where a row's link goes, or `null` for a commercial component, which has no page. */
export function componentRowHref(row: CatalogComponentRow): string | null {
    return row.type === 'release'
        ? `/catalog/components/release/${encodeURIComponent(row.id)}`
        : null;
}
